/**
 * Contract API PPW pricing — `GET /api/Contract/PPWDetails?opportunityId&stateCode&utilityId`.
 * Rows come from `wolf.sp_Search_Ppw_Details` and carry `Expected_PPW` per rate/escalator/multiplier,
 * so PPW here is a lookup instead of the Excel `slope × multiplier + intercept` fit.
 */
import {
    quotePartnerFromTables,
    type PpwLiveQuoteResult,
    type PpwMatrixCell,
    type PpwPartnerId,
    type PpwPartnerMatrix,
    type PpwPartnerQuote,
} from './preContractPpwPricing';

/** One normalized `Finance_Partner_Trinity_PPW` row. */
export type PpwDetailsRow = {
    partner: PpwPartnerId | null;
    financePartner: string;
    state: string;
    utility: string;
    purchaseMethodType: string;
    vintageName: string;
    /** Fraction, e.g. 0.0299 for a 2.99% escalator. */
    escalator: number | null;
    multiplier: number | null;
    /** $/kWh, e.g. 0.162. */
    ppaRate: number | null;
    expectedPpw: number | null;
    effectiveStartDate: string | null;
};

export type PpwDetailsDataset = {
    rows: PpwDetailsRow[];
    byPartner: Record<PpwPartnerId, PpwDetailsRow[]>;
    /** `Finance_Partner` values the rail has no card for. */
    unmappedPartners: string[];
};

/** Why the rail fell back to Excel, so the UI can flag it. */
export type PpwDetailsFallbackReason =
    | 'missing-opportunity'
    | 'missing-state'
    | 'no-records'
    | 'no-partner-rows'
    | 'request-failed';

/** Settled outcome of a PPWDetails request. */
export type PpwDetailsResolvedState =
    | { status: 'ready'; dataset: PpwDetailsDataset }
    | { status: 'fallback'; reason: PpwDetailsFallbackReason; message?: string };

export type PpwDetailsFetchState = { status: 'loading' } | PpwDetailsResolvedState;

const PARTNER_ALIASES: { id: PpwPartnerId; pattern: RegExp }[] = [
    { id: 'LightReach', pattern: /light\s*reach|palmetto/i },
    { id: 'Enfin', pattern: /enfin/i },
];

const EMPTY_BY_PARTNER = (): Record<PpwPartnerId, PpwDetailsRow[]> => ({
    Enfin: [],
    LightReach: [],
});

function nearlyEqual(a: number, b: number, eps = 1e-6): boolean {
    return Math.abs(a - b) <= eps;
}

function uniqueSorted(values: number[]): number[] {
    const out: number[] = [];
    values
        .slice()
        .sort((a, b) => a - b)
        .forEach((value) => {
            if (!out.some((existing) => nearlyEqual(existing, value))) out.push(value);
        });
    return out;
}

function text(value: unknown): string {
    return String(value ?? '').trim();
}

/** Percent-to-fraction division leaves float dust that would break cell keys. */
function round6(value: number): number {
    return Math.round(value * 1e6) / 1e6;
}

function resolvePartnerId(financePartner: string): PpwPartnerId | null {
    return PARTNER_ALIASES.find(({ pattern }) => pattern.test(financePartner))?.id ?? null;
}

/**
 * Escalators arrive as text — `"2.99"`, `"2.99%"` or `"0.0299"`.
 * Anything at or above 0.5 is a percentage, since no escalator is 50%.
 */
function parseEscalator(value: unknown): number | null {
    const raw = text(value).replace(/%/g, '');
    if (!raw) return null;
    const num = Number(raw);
    if (!Number.isFinite(num) || num < 0) return null;
    return round6(num >= 0.5 ? num / 100 : num);
}

/** Rates arrive as dollars (`0.162`) or cents (`16.2`). */
function parseRate(value: unknown): number | null {
    if (value == null || text(value) === '') return null;
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return null;
    return round6(num >= 1 ? num / 100 : num);
}

/** Multipliers arrive as a ratio (`1.25`) or as kWh/kW (`1250`). */
function parseMultiplier(value: unknown): number | null {
    if (value == null || text(value) === '') return null;
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return null;
    return round6(num > 10 ? num / 1000 : num);
}

function parsePpw(value: unknown): number | null {
    if (value == null || text(value) === '') return null;
    const num = Number(value);
    return Number.isFinite(num) && num > 0 ? num : null;
}

function normalizeRow(raw: Record<string, unknown>): PpwDetailsRow {
    const financePartner = text(raw.Finance_Partner ?? raw.finance_Partner ?? raw.financePartner);
    return {
        partner: resolvePartnerId(financePartner),
        financePartner,
        state: text(raw.State ?? raw.state).toUpperCase(),
        utility: text(raw.Utility ?? raw.utility),
        purchaseMethodType: text(raw.Purchase_Method_Type ?? raw.purchaseMethodType),
        vintageName: text(raw.Vintage_Name ?? raw.vintageName ?? raw.Name ?? raw.name),
        escalator: parseEscalator(raw.Escalator ?? raw.escalator),
        multiplier: parseMultiplier(raw.Multiplier ?? raw.multiplier),
        ppaRate: parseRate(raw.PPA_Rate ?? raw.ppA_Rate ?? raw.ppaRate),
        expectedPpw: parsePpw(raw.Expected_PPW ?? raw.expected_PPW ?? raw.expectedPpw),
        effectiveStartDate: text(raw.Effective_Start_Date ?? raw.effectiveStartDate) || null,
    };
}

export function buildPpwDetailsDataset(payload: unknown): PpwDetailsDataset {
    const list = Array.isArray(payload)
        ? payload
        : Array.isArray((payload as { data?: unknown })?.data)
          ? ((payload as { data: unknown[] }).data as unknown[])
          : [];

    const rows = list
        .filter((item): item is Record<string, unknown> => item != null && typeof item === 'object')
        .map(normalizeRow);

    const byPartner = EMPTY_BY_PARTNER();
    const unmappedPartners: string[] = [];
    rows.forEach((row) => {
        if (row.partner) {
            byPartner[row.partner].push(row);
        } else if (row.financePartner && !unmappedPartners.includes(row.financePartner)) {
            unmappedPartners.push(row.financePartner);
        }
    });

    return { rows, byPartner, unmappedPartners };
}

/** Rows that can price something: escalator, rate and PPW all present. */
function pricableRows(rows: PpwDetailsRow[]): PpwDetailsRow[] {
    return rows.filter(
        (row) => row.escalator != null && row.ppaRate != null && row.expectedPpw != null,
    );
}

export function ppwDetailsEscalators(rows: PpwDetailsRow[]): number[] {
    return uniqueSorted(pricableRows(rows).map((row) => row.escalator as number));
}

export function ppwDetailsRates(rows: PpwDetailsRow[], escalator?: number): number[] {
    return uniqueSorted(
        pricableRows(rows)
            .filter(
                (row) =>
                    escalator == null || nearlyEqual(row.escalator as number, escalator, 1e-4),
            )
            .map((row) => row.ppaRate as number),
    );
}

/**
 * The stored procedure can return several multiplier bands per rate, so pick the band
 * closest to the design multiplier rather than assuming a single row.
 */
function closestByMultiplier(rows: PpwDetailsRow[], multiplier: number): PpwDetailsRow | null {
    if (!rows.length) return null;
    let best = rows[0];
    let bestDistance = Number.POSITIVE_INFINITY;
    rows.forEach((row) => {
        const distance =
            row.multiplier == null ? Number.MAX_SAFE_INTEGER : Math.abs(row.multiplier - multiplier);
        if (distance < bestDistance) {
            best = row;
            bestDistance = distance;
        }
    });
    return best;
}

type PpwDetailsFit = {
    ppw: number;
    rate: number;
    interpolated: boolean;
    row: PpwDetailsRow;
};

/** Exact rate row, or a linear blend of the bracketing rates at the same escalator. */
function resolvePpwFit(
    rows: PpwDetailsRow[],
    rate: number,
    escalator: number,
    multiplier: number,
): PpwDetailsFit | null {
    const atEscalator = pricableRows(rows).filter((row) =>
        nearlyEqual(row.escalator as number, escalator, 1e-4),
    );
    if (!atEscalator.length) return null;

    const exact = closestByMultiplier(
        atEscalator.filter((row) => nearlyEqual(row.ppaRate as number, rate, 1e-6)),
        multiplier,
    );
    if (exact) {
        return {
            ppw: exact.expectedPpw as number,
            rate: exact.ppaRate as number,
            interpolated: false,
            row: exact,
        };
    }

    const rates = uniqueSorted(atEscalator.map((row) => row.ppaRate as number));
    let lower: number | null = null;
    let upper: number | null = null;
    rates.forEach((value) => {
        if (value <= rate) lower = value;
        if (value >= rate && upper == null) upper = value;
    });

    const rowAt = (value: number) =>
        closestByMultiplier(
            atEscalator.filter((row) => nearlyEqual(row.ppaRate as number, value, 1e-6)),
            multiplier,
        );

    if (lower != null && upper != null && !nearlyEqual(lower, upper)) {
        const lowerRow = rowAt(lower);
        const upperRow = rowAt(upper);
        if (lowerRow && upperRow) {
            const t = (rate - lower) / (upper - lower);
            const lowerPpw = lowerRow.expectedPpw as number;
            const upperPpw = upperRow.expectedPpw as number;
            return {
                ppw: lowerPpw + t * (upperPpw - lowerPpw),
                rate,
                interpolated: true,
                row: upperRow,
            };
        }
    }

    // Outside the returned range — clamp to the nearest edge row, flagged as derived.
    const edge = (lower != null ? rowAt(lower) : null) ?? (upper != null ? rowAt(upper) : null);
    if (!edge) return null;
    return {
        ppw: edge.expectedPpw as number,
        rate: edge.ppaRate as number,
        interpolated: true,
        row: edge,
    };
}

export function quotePartnerFromPpwDetails(input: {
    partner: PpwPartnerId;
    rows: PpwDetailsRow[];
    rate: number;
    escalator: number;
    multiplier: number;
    systemWatts: number;
    annualProductionKwh: number;
}): PpwPartnerQuote {
    const { partner, rows, rate, escalator, multiplier, systemWatts, annualProductionKwh } = input;

    const unavailable = (reason: string): PpwPartnerQuote => ({
        partner,
        available: false,
        reason,
        escalator,
        rate,
        interpolated: false,
        slope: null,
        intercept: null,
        ppw: null,
        epc: null,
        estimatedMonthly: null,
    });

    const pricable = pricableRows(rows);
    if (!pricable.length) return unavailable(`No ${partner} pricing returned`);

    const escalators = ppwDetailsEscalators(rows);
    if (!escalators.some((value) => nearlyEqual(value, escalator, 1e-4))) {
        return unavailable(`Escalator ${(escalator * 100).toFixed(2)}% not offered`);
    }

    const fit = resolvePpwFit(rows, rate, escalator, multiplier);
    if (!fit) return unavailable('Could not resolve rate row');

    const epc = systemWatts > 0 ? fit.ppw * systemWatts : null;
    const estimatedMonthly =
        annualProductionKwh > 0 && rate > 0 ? (rate * annualProductionKwh) / 12 : null;

    return {
        partner,
        available: true,
        escalator,
        rate,
        interpolated: fit.interpolated,
        slope: null,
        intercept: null,
        ppw: Math.round(fit.ppw * 1000) / 1000,
        epc: epc != null ? Math.round(epc * 100) / 100 : null,
        estimatedMonthly:
            estimatedMonthly != null ? Math.round(estimatedMonthly * 100) / 100 : null,
    };
}

/** PPW for one module multiplier at the selected rate and escalator. Prefers PPWDetails. */
export function quoteModulePartnerPpw(input: {
    partner: PpwPartnerId;
    state: string;
    rate: number;
    escalator: number;
    multiplier: number;
    dataset?: PpwDetailsDataset | null;
}): number | null {
    const { partner, state, rate, escalator, multiplier, dataset } = input;
    if (!(rate > 0) || !(multiplier > 0)) return null;

    const rows = dataset?.byPartner[partner] ?? [];
    if (rows.length) {
        const live = quotePartnerFromPpwDetails({
            partner,
            rows,
            rate,
            escalator,
            multiplier,
            systemWatts: 0,
            annualProductionKwh: 0,
        });
        if (live.available && live.ppw != null) return live.ppw;
    }

    const excel = quotePartnerFromTables({
        partner,
        state,
        rate,
        escalator,
        multiplier,
        systemWatts: 0,
        annualProductionKwh: 0,
    });
    return excel.available ? excel.ppw : null;
}

/** Full rate × escalator grid for one partner from API rows. */
export function buildPpwDetailsMatrix(input: {
    partner: PpwPartnerId;
    rows: PpwDetailsRow[];
    multiplier: number;
    systemWatts: number;
    annualProductionKwh: number;
    extraRate?: number | null;
}): PpwPartnerMatrix {
    const { partner, rows, multiplier, systemWatts, annualProductionKwh, extraRate } = input;
    const escalators = ppwDetailsEscalators(rows);
    const rateSet = ppwDetailsRates(rows);
    if (extraRate != null && Number.isFinite(extraRate) && extraRate > 0) {
        if (!rateSet.some((value) => nearlyEqual(value, extraRate))) rateSet.push(extraRate);
    }
    const rates = uniqueSorted(rateSet);

    const cells: PpwMatrixCell[][] = rates.map((rate) =>
        escalators.map((escalator) => {
            const quote = quotePartnerFromPpwDetails({
                partner,
                rows,
                rate,
                escalator,
                multiplier,
                systemWatts,
                annualProductionKwh,
            });
            return {
                rate,
                escalator,
                ppw: quote.ppw,
                epc: quote.epc,
                estimatedMonthly: quote.estimatedMonthly,
                available: quote.available,
                interpolated: quote.interpolated,
            };
        }),
    );

    return { partner, rates, escalators, cells };
}

/** True when at least one rail partner has pricable rows. */
export function hasPpwDetailsPricing(dataset: PpwDetailsDataset): boolean {
    return (['Enfin', 'LightReach'] as PpwPartnerId[]).some(
        (partner) => pricableRows(dataset.byPartner[partner]).length > 0,
    );
}

/**
 * Replace the Excel-derived rate/escalator options and partner quotes on a live result,
 * keeping the form-derived design metrics (size, production, multiplier, target/min PPA).
 */
export function applyPpwDetailsToLiveQuotes(
    result: PpwLiveQuoteResult,
    dataset: PpwDetailsDataset,
    options: { escalator: number; rateOverride?: number | null },
): PpwLiveQuoteResult {
    const partners: PpwPartnerId[] = ['Enfin', 'LightReach'];
    const allRows = partners.flatMap((partner) => dataset.byPartner[partner]);

    const availableEscalators = ppwDetailsEscalators(allRows);
    const activeEscalator = availableEscalators.some((value) =>
        nearlyEqual(value, options.escalator, 1e-4),
    )
        ? options.escalator
        : (availableEscalators.find((value) => nearlyEqual(value, 0.0299, 1e-4)) ??
          availableEscalators[availableEscalators.length - 1] ??
          options.escalator);

    const availableRates = ppwDetailsRates(allRows, activeEscalator);
    const rate =
        options.rateOverride != null &&
        Number.isFinite(options.rateOverride) &&
        options.rateOverride > 0
            ? options.rateOverride
            : result.targetPpaRate || result.rate || result.allInRate;

    return {
        ...result,
        rate,
        escalator: activeEscalator,
        source: 'contract-api',
        availableEscalators,
        availableRates,
        quotes: partners.map((partner) =>
            quotePartnerFromPpwDetails({
                partner,
                rows: dataset.byPartner[partner],
                rate,
                escalator: activeEscalator,
                multiplier: result.multiplier,
                systemWatts: result.systemWatts,
                annualProductionKwh: result.annualProductionKwh,
            }),
        ),
    };
}

/** Fetch + normalize PPWDetails, resolving to a fallback state instead of throwing. */
export async function fetchPpwDetails(params: {
    opportunityId: string | number | null | undefined;
    stateCode: string;
    utilityId: string | number | null | undefined;
}): Promise<PpwDetailsResolvedState> {
    const opportunityId = Number(params.opportunityId);
    if (!Number.isFinite(opportunityId) || opportunityId <= 0) {
        return { status: 'fallback', reason: 'missing-opportunity' };
    }
    const stateCode = String(params.stateCode ?? '').trim();
    if (!stateCode) return { status: 'fallback', reason: 'missing-state' };

    try {
        // Imported lazily so the pricing math stays free of the MSAL axios chain.
        const { default: contractService } = await import(
            '../../services/oneButtonServices/contractService'
        );
        const payload = await contractService.getPPWDetails(
            opportunityId,
            stateCode,
            params.utilityId,
        );
        const dataset = buildPpwDetailsDataset(payload);
        if (!dataset.rows.length) return { status: 'fallback', reason: 'no-records' };
        if (!hasPpwDetailsPricing(dataset)) {
            return { status: 'fallback', reason: 'no-partner-rows' };
        }
        return { status: 'ready', dataset };
    } catch (error: unknown) {
        return {
            status: 'fallback',
            reason: 'request-failed',
            message: error instanceof Error ? error.message : undefined,
        };
    }
}

export function describePpwDetailsFallback(state: {
    reason: PpwDetailsFallbackReason;
    message?: string;
}): string {
    switch (state.reason) {
        case 'missing-opportunity':
            return 'Opportunity is not loaded yet, so live PPW could not be requested.';
        case 'missing-state':
            return 'Pick an install state to request live PPW.';
        case 'no-records':
            return 'PPWDetails returned no rows for this opportunity, state and utility.';
        case 'no-partner-rows':
            return 'PPWDetails returned rows, but none for LightReach or Enfin.';
        case 'request-failed':
        default:
            return state.message
                ? `PPWDetails request failed: ${state.message}`
                : 'PPWDetails request failed.';
    }
}
